<template>
  <div>
    <b-card
      v-for="file in pagedFiles"
      :key="file.id"
      no-body
      class="existing-file-card mb-2"
    >
      <b-card-body
        class="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between py-2"
      >
        <div class="mr-sm-2 mb-2 mb-sm-0 existing-file-info">
          <h5 class="font-weight-bold mb-1 existing-file-name">
            {{ file.display_name || file.name }}
          </h5>
          <div class="d-flex flex-wrap align-items-center small text-secondary">
            <!-- When a BA is uploading for a tradie, show that file's approval
                 status for the BA's own company (which can differ per company)
                 rather than the shared TradiePass verification status. -->
            <company-approval-badge
              v-if="showCompanyApproval"
              :status="file.company_approval"
              :reason="file.company_approval_reason || ''"
              :set-date="file.company_approval_at || ''"
              :can-edit="false"
              class="mr-2"
            />
            <b-badge
              v-else-if="statusLabel(file)"
              pill
              :variant="statusVariant(file)"
              class="text-capitalize mr-2"
            >
              {{ statusLabel(file) }}
            </b-badge>
            <span v-if="viewOnly" class="mr-2">{{ expiryLabel(file) }}</span>
            <span v-if="lastUpdated(file)">
              Last updated {{ lastUpdated(file) }}
            </span>
          </div>
        </div>
        <b-button
          v-if="!viewOnly && file.canEditInModal !== false"
          variant="outline-primary"
          size="sm"
          class="flex-shrink-0"
          @click="$emit('edit', file)"
        >
          Edit
        </b-button>
        <b-button
          v-else
          variant="outline-primary"
          size="sm"
          class="flex-shrink-0"
          @click="$emit('view', file)"
        >
          View
        </b-button>
      </b-card-body>
    </b-card>

    <b-pagination
      v-if="files.length > perPage"
      v-model="currentPage"
      :total-rows="files.length"
      :per-page="perPage"
      align="center"
      size="sm"
      class="mt-3 mb-2"
    />
  </div>
</template>

<script>
import CompanyApprovalBadge from '@/components/badges/CompanyApprovalBadge.vue'

export default {
  name: 'ExistingFileList',

  components: {
    CompanyApprovalBadge,
  },

  props: {
    files: {
      type: Array,
      default: () => [],
    },
    perPage: {
      type: Number,
      default: 5,
    },
    // BA-uploading-for-tradie: show each file's per-company approval status
    // instead of its TradiePass verification status.
    showCompanyApproval: {
      type: Boolean,
      default: false,
    },
    // Always offer View, never Edit.
    viewOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      currentPage: 1,
    }
  },

  computed: {
    pagedFiles() {
      const start = (this.currentPage - 1) * this.perPage
      return this.files.slice(start, start + this.perPage)
    },
  },

  watch: {
    // Reset to the first page whenever the list changes (e.g. a new lookup).
    files() {
      this.currentPage = 1
    },
  },

  methods: {
    statusLabel(file) {
      const status = file && file.status
      if (!status) return ''
      return status
        .trim()
        .toLowerCase()
        .replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase()))
    },

    statusVariant(file) {
      switch (this.statusLabel(file)) {
        case 'Valid':
          return 'success'
        case 'Pending Approval':
          return 'warning'
        case 'Rejected':
          return 'danger'
        default:
          return 'secondary'
      }
    },

    // Tradie files carry a pre-formatted dd/mm/yyyy `date_expiry` ('-' when
    // the file has none).
    expiryLabel(file) {
      const expiry = file && file.date_expiry
      return expiry && expiry !== '-' ? `Expires ${expiry}` : 'No expiry'
    },

    lastUpdated(file) {
      const date =
        file && (file.updated_at || file.status_updated_at || file.created_at)
      return date ? this.getTimeDiff(date) : ''
    },
  },
}
</script>

<style lang="scss" scoped>
.existing-file-card {
  border: 1px solid #e1e6ea;
  border-radius: 8px;
}

/* Let the text column shrink within the flex row so a long, unbroken file
   name wraps instead of pushing the Edit/View button out of the card. */
.existing-file-info {
  min-width: 0;
  flex: 1 1 auto;
}

.existing-file-name {
  overflow-wrap: anywhere;
  word-break: break-word;
}
</style>
